import { FC, SyntheticEvent } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import type { MediaId, MediaVariantName } from 'models/Media';
import { useMediaUrl } from 'hooks/useMediaUrl';

interface Props {
  mediaId: MediaId;
  // A smaller copy to show instead of the original, when the server has one.
  variant?: MediaVariantName;
  alt?: string;
  className?: string;
  // Wrap the image in a link that opens the full image in a new tab.
  linkToFull?: boolean;
  wrapperClassName?: string;
  onLoad?: (event: SyntheticEvent<HTMLImageElement>) => void;
}

// An <img> for a stored media file. Shows a spinner while the URL loads and a
// warning icon if it can't be loaded.
export const MediaImage: FC<Props> = ({
  mediaId,
  variant,
  alt = '',
  className,
  linkToFull = false,
  wrapperClassName,
  onLoad,
}) => {
  const { url, originalUrl, status, onError } = useMediaUrl(mediaId, variant);

  const content =
    status === 'ready' && url ? (
      <img
        src={url}
        alt={alt}
        className={className}
        onError={onError}
        onLoad={onLoad}
      />
    ) : (
      <div className={classNames(styles.placeholder, className)}>
        <FontAwesomeIcon
          icon={status === 'error' ? faTriangleExclamation : faSpinner}
          className={status === 'error' ? undefined : 'animate-spin'}
          aria-hidden="true"
        />
      </div>
    );

  if (linkToFull && status === 'ready' && originalUrl) {
    return (
      <a
        href={originalUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={wrapperClassName}
      >
        {content}
      </a>
    );
  }

  return wrapperClassName ? (
    <div className={wrapperClassName}>{content}</div>
  ) : (
    content
  );
};

const styles = {
  placeholder:
    'flex items-center justify-center bg-surface-field text-ink-tertiary',
};
